import { useState, type FormEvent } from "react";
import { Archive, CheckCircle2, Edit2, FolderPlus, RotateCcw, Save, Users, X } from "lucide-react";
import { trpc } from "@/lib/trpc";

type GroupForm = {
  slug: string;
  name: string;
  description: string;
  topic: string;
};

const EMPTY_FORM: GroupForm = { slug: "", name: "", description: "", topic: "" };

const inputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  minHeight: 42,
  padding: "0.65rem 0.75rem",
  border: "1px solid rgba(255,255,255,0.13)",
  borderRadius: "0.55rem",
  background: "rgba(255,255,255,0.045)",
  color: "#F8FAFC",
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: "var(--chi-body-compact)",
  outline: "none",
};

const labelStyle: React.CSSProperties = {
  display: "grid",
  gap: "0.35rem",
  color: "#94A3B8",
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: "var(--chi-body-compact)",
  fontWeight: 650,
};

function errorMessage(error: unknown, fallback: string) {
  return error instanceof Error && error.message ? error.message : fallback;
}

export default function MemberGroupManagementView() {
  const utils = trpc.useUtils();
  const groups = trpc.members.adminGroups.useQuery(undefined, { retry: false });
  const createGroup = trpc.members.createAdminGroup.useMutation();
  const updateGroup = trpc.members.updateAdminGroup.useMutation();
  const archiveGroup = trpc.members.archiveAdminGroup.useMutation();
  const activateGroup = trpc.members.activateAdminGroup.useMutation();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<GroupForm>(EMPTY_FORM);
  const [notice, setNotice] = useState<{ text: string; tone: "success" | "error" } | null>(null);

  const resetForm = () => {
    setEditorOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const beginCreate = () => {
    setNotice(null);
    setEditorOpen(true);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const beginEdit = (group: NonNullable<typeof groups.data>[number]) => {
    setNotice(null);
    setEditorOpen(true);
    setEditingId(group.id);
    setForm({
      slug: group.slug,
      name: group.name,
      description: group.description,
      topic: group.topic,
    });
  };

  const updateField = (field: keyof GroupForm, value: string) => {
    setForm(current => ({ ...current, [field]: value }));
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setNotice(null);
    const content = {
      slug: form.slug.trim().toLowerCase(),
      name: form.name.trim(),
      description: form.description.trim(),
      topic: form.topic.trim(),
    };
    try {
      if (editingId === null) {
        await createGroup.mutateAsync(content);
        setNotice({ text: "Group created and published to members.", tone: "success" });
      } else {
        await updateGroup.mutateAsync({ groupId: editingId, ...content });
        setNotice({ text: "Group details saved.", tone: "success" });
      }
      resetForm();
      await utils.members.adminGroups.invalidate();
    } catch (error) {
      setNotice({ text: errorMessage(error, "The group could not be saved."), tone: "error" });
    }
  };

  const changeVisibility = async (group: NonNullable<typeof groups.data>[number]) => {
    setNotice(null);
    if (group.isActive) {
      const confirmed = window.confirm(
        `Archive “${group.name}”? Members will no longer see or enter it, but existing memberships and posts will be preserved.`,
      );
      if (!confirmed) return;
    }
    try {
      if (group.isActive) await archiveGroup.mutateAsync({ groupId: group.id });
      else await activateGroup.mutateAsync({ groupId: group.id });
      setNotice({
        text: group.isActive
          ? "Group archived. Existing memberships and posts were preserved."
          : "Group activated and visible to members again.",
        tone: "success",
      });
      await utils.members.adminGroups.invalidate();
    } catch (error) {
      setNotice({ text: errorMessage(error, "The group status could not be changed."), tone: "error" });
    }
  };

  const saving = createGroup.isPending || updateGroup.isPending;
  const changingVisibility = archiveGroup.isPending || activateGroup.isPending;

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "clamp(1rem, 2.5vw, 2rem)" }}>
      <div style={{ width: "min(100%, 1120px)", margin: "0 auto" }}>
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem", flexWrap: "wrap", marginBottom: "1.4rem" }}>
          <div>
            <p style={{ margin: 0, color: "#86EFAC", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase" }}>Member space</p>
            <h1 style={{ margin: "0.35rem 0 0", color: "#FFF", fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2rem, 4vw, 3rem)", letterSpacing: "0.025em", fontWeight: 400 }}>Group curation</h1>
            <p style={{ maxWidth: 650, margin: "0.45rem 0 0", color: "#94A3B8", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.6 }}>
              Keep member conversations current. Archive a group when it is no longer relevant; its members and posts remain safe if you bring it back.
            </p>
          </div>
          <button type="button" onClick={beginCreate} style={{ display: "inline-flex", alignItems: "center", gap: "0.45rem", minHeight: 42, padding: "0.65rem 0.85rem", border: 0, borderRadius: "0.55rem", color: "#FFF", background: "linear-gradient(135deg,#3A9B70,#67C58E)", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, cursor: "pointer" }}>
            <FolderPlus size={16} /> New group
          </button>
        </header>

        {notice && (
          <div role={notice.tone === "error" ? "alert" : "status"} style={{ marginBottom: "1rem", padding: "0.75rem 0.9rem", border: `1px solid ${notice.tone === "error" ? "rgba(248,113,113,.3)" : "rgba(134,239,172,.28)"}`, borderRadius: "0.55rem", color: notice.tone === "error" ? "#FCA5A5" : "#BBF7D0", background: notice.tone === "error" ? "rgba(127,29,29,.2)" : "rgba(20,83,45,.18)", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)" }}>
            {notice.text}
          </div>
        )}

        {editorOpen ? (
          <form onSubmit={save} style={{ display: "grid", gap: "1rem", marginBottom: "1.25rem", padding: "1.15rem", border: "1px solid rgba(134,239,172,.2)", borderRadius: "0.8rem", background: "linear-gradient(145deg,rgba(20,55,49,.32),rgba(13,18,32,.8))" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem" }}>
              <h2 style={{ margin: 0, color: "#F8FAFC", fontFamily: "'Space Grotesk', sans-serif", fontSize: "1rem" }}>{editingId === null ? "Create a member group" : "Edit group"}</h2>
              <button type="button" aria-label="Close group editor" onClick={resetForm} style={{ display: "grid", placeItems: "center", width: 32, height: 32, border: 0, color: "#94A3B8", background: "transparent", cursor: "pointer" }}><X size={17} /></button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: "0.85rem" }}>
              <label style={labelStyle}>Group name
                <input required maxLength={140} value={form.name} onChange={event => updateField("name", event.target.value)} placeholder="Human-Centered Leadership" style={inputStyle} />
              </label>
              <label style={labelStyle}>Topic label
                <input required maxLength={120} value={form.topic} onChange={event => updateField("topic", event.target.value)} placeholder="Leadership" style={inputStyle} />
              </label>
            </div>
            <label style={labelStyle}>URL slug
              <input required pattern="[a-z0-9]+(?:-[a-z0-9]+)*" title="Use lowercase letters, numbers, and single hyphens." maxLength={120} value={form.slug} onChange={event => updateField("slug", event.target.value)} placeholder="human-centered-leadership" style={inputStyle} />
            </label>
            <label style={labelStyle}>Description
              <textarea required maxLength={2000} rows={3} value={form.description} onChange={event => updateField("description", event.target.value)} placeholder="What will members explore together?" style={{ ...inputStyle, resize: "vertical" }} />
            </label>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.6rem", flexWrap: "wrap" }}>
              <button type="button" onClick={resetForm} style={{ minHeight: 40, padding: "0.6rem 0.8rem", border: "1px solid rgba(255,255,255,.13)", borderRadius: "0.5rem", color: "#CBD5E1", background: "transparent", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", cursor: "pointer" }}>Cancel</button>
              <button type="submit" disabled={saving} style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", minHeight: 40, padding: "0.6rem 0.85rem", border: 0, borderRadius: "0.5rem", color: "#FFF", background: "linear-gradient(135deg,#A73889,#E91E8C)", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, cursor: saving ? "wait" : "pointer", opacity: saving ? 0.65 : 1 }}>
                <Save size={15} /> {saving ? "Saving…" : editingId === null ? "Create group" : "Save changes"}
              </button>
            </div>
          </form>
        ) : null}

        {groups.isLoading ? (
          <div style={{ padding: "2rem", color: "#94A3B8", fontFamily: "'Space Grotesk', sans-serif" }}>Loading groups…</div>
        ) : groups.isError ? (
          <div role="alert" style={{ padding: "1rem", border: "1px solid rgba(248,113,113,.25)", borderRadius: "0.7rem", color: "#FCA5A5", background: "rgba(127,29,29,.16)", fontFamily: "'Space Grotesk', sans-serif" }}>Groups could not be loaded. Refresh the page to try again.</div>
        ) : (groups.data ?? []).length === 0 ? (
          <div style={{ padding: "2rem", border: "1px dashed rgba(255,255,255,.15)", borderRadius: "0.7rem", color: "#94A3B8", fontFamily: "'Space Grotesk', sans-serif" }}>No groups yet. Create the first member conversation space above.</div>
        ) : (
          <div style={{ display: "grid", gap: "0.7rem" }}>
            {(groups.data ?? []).map(group => (
              <article key={group.id} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: "1rem", alignItems: "center", padding: "1rem 1.1rem", border: `1px solid ${group.isActive ? "rgba(134,239,172,.16)" : "rgba(148,163,184,.13)"}`, borderRadius: "0.7rem", background: group.isActive ? "rgba(13,31,31,.64)" : "rgba(255,255,255,.025)", opacity: group.isActive ? 1 : 0.76 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.55rem", flexWrap: "wrap" }}>
                    <h2 style={{ margin: 0, color: "#F8FAFC", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.98rem" }}>{group.name}</h2>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", padding: "0.2rem 0.45rem", borderRadius: 99, color: group.isActive ? "#86EFAC" : "#94A3B8", background: group.isActive ? "rgba(134,239,172,.1)" : "rgba(148,163,184,.1)", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.68rem", fontWeight: 750 }}>{group.isActive ? <><CheckCircle2 size={11} /> Active</> : "Archived"}</span>
                  </div>
                  <p style={{ margin: "0.35rem 0 0", color: "#86EFAC", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.72rem" }}>{group.topic} <span style={{ color: "#64748B" }}>· /{group.slug}</span></p>
                  <p style={{ maxWidth: 720, margin: "0.45rem 0 0", color: "#94A3B8", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>{group.description}</p>
                  <p style={{ display: "flex", alignItems: "center", gap: "0.35rem", margin: "0.55rem 0 0", color: "#64748B", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.72rem" }}><Users size={13} /> {group.memberCount} member{group.memberCount === 1 ? "" : "s"} · {group.postCount} post{group.postCount === 1 ? "" : "s"}</p>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.45rem", flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <button type="button" onClick={() => beginEdit(group)} style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", minHeight: 36, padding: "0.45rem 0.6rem", border: "1px solid rgba(255,255,255,.13)", borderRadius: "0.45rem", color: "#CBD5E1", background: "transparent", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.72rem", cursor: "pointer" }}><Edit2 size={13} /> Edit</button>
                  <button type="button" disabled={changingVisibility} onClick={() => void changeVisibility(group)} style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", minHeight: 36, padding: "0.45rem 0.6rem", border: `1px solid ${group.isActive ? "rgba(248,113,113,.25)" : "rgba(134,239,172,.25)"}`, borderRadius: "0.45rem", color: group.isActive ? "#FCA5A5" : "#86EFAC", background: "transparent", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.72rem", cursor: changingVisibility ? "wait" : "pointer", opacity: changingVisibility ? 0.6 : 1 }}>{group.isActive ? <><Archive size={13} /> Archive</> : <><RotateCcw size={13} /> Activate</>}</button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}